import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Paper, 
  Alert,
  CircularProgress
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { login, loadUser } from '../../features/auth/authSlice';
import { AppDispatch, RootState } from '../../store';
import { useBrand } from '../../brands/BrandProvider';

const LoginPage: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error } = useSelector((state: RootState) => state.auth);
  const brand = useBrand();
  const isCustomBrand = brand.key !== 'platform';
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const result = await dispatch(login(formData));
    
    if (login.fulfilled.match(result)) {
      await dispatch(loadUser());
      const next = searchParams.get('next');
      const safeNext =
        next && next.startsWith('/') && !next.startsWith('//') && !next.includes('://') ? next : null;
      navigate(safeNext || '/dashboard');
    }
  };
  
  return (
    <Container component="main" maxWidth="xs" sx={{ py: { xs: 6, md: 8 } }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
          backgroundColor: alpha('#ffffff', 0.92),
          backdropFilter: 'blur(4px)',
          boxShadow: `0 18px 40px ${alpha(theme.palette.primary.dark, 0.12)}`,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography
            component="h1"
            variant="h5"
            gutterBottom
            sx={{ fontFamily: '"Sora", "Manrope", sans-serif' }}
          >
            {brand.copy.auth.loginTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {brand.copy.auth.loginSubtitle}
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Senha"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{
                mt: 3,
                mb: 2,
                py: 1.2,
                fontWeight: 700,
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} sx={{ color: theme.palette.primary.contrastText }} /> : 'Entrar'}
            </Button>
            <Grid container spacing={1}>
              <Grid item xs>
                <Link to="/recuperar-senha">
                  <Typography variant="body2" color="primary">
                    Esqueceu a senha?
                  </Typography>
                </Link>
              </Grid>
              {!isCustomBrand && (
                <Grid item xs style={{ textAlign: 'right' }}>
                  <Link to="/login-subparoquia">
                    <Typography variant="body2" color="primary">
                      Acesso antigo por e-mail
                    </Typography>
                  </Link>
                </Grid>
              )}
            </Grid>
            {!isCustomBrand || brand.key === 'zuadaria' ? (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" component="span">
                  Ainda não tem conta?{' '}
                </Typography>
                <Link to="/cadastro-paroquia">
                  <Typography variant="body2" color="primary" component="span" sx={{ fontWeight: 600 }}>
                    {brand.key === 'zuadaria' ? 'Cadastre sua casa' : 'Cadastre sua organização'}
                  </Typography>
                </Link>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default LoginPage;
